import React, { useState } from 'react';
import { 
  School, 
  BookOpen, 
  Users, 
  MapPin, 
  Leaf, 
  Sprout, 
  Zap, 
  Award, 
  Calendar, 
  ArrowRight,
  Check,
  Globe,
  Network,
  Lightbulb,
  Brain,
  Star,
  CheckCircle2,
  Mail,
  Send,
  Heart,
  HelpCircle,
  AlertTriangle,
  ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeader from '../components/shared/SectionHeader';
import { Card, Badge, Button } from '../components/ui';

// Campus data
const campusData = {
  overview: {
    description: "REGEN Campus is our innovative educational hub that combines hands-on learning with cutting-edge technology to create a comprehensive training ecosystem for sustainable agriculture.",
    mission: "To empower farmers and agricultural professionals with the knowledge and skills needed for regenerative farming practices and sustainable food systems."
  },
  locations: [
    {
      name: "Edward's Hamlet",
      location: "Jinja, Uganda",
      coordinates: {
        latitude: 0.4478,
        longitude: 33.2026
      },
      size: "12 acres",
      focus: "Sustainable agriculture and farmer support",
      features: [
        "Cooperative of 273 local farmers",
        "Hands-on learning opportunities",
        "FabLab for DIY sensors",
        "Community technology access"
      ]
    }
  ],
  programs: [
    {
      title: "Regenerative Agriculture",
      duration: "12 weeks",
      description: "Comprehensive training in soil health, water conservation, biodiversity enhancement, and integrated pest management.",
      topics: [
        "Soil health management",
        "Water conservation",
        "Biodiversity enhancement",
        "Integrated pest management"
      ],
      icon: <Leaf className="h-6 w-6 text-green-600" />
    },
    {
      title: "Technology Integration",
      duration: "8 weeks",
      description: "Learn to integrate smart farming techniques, deploy IoT sensors, analyze data, and implement precision agriculture.",
      topics: [
        "Smart farming techniques",
        "IoT sensor deployment",
        "Data analysis",
        "Precision agriculture"
      ],
      icon: <Zap className="h-6 w-6 text-blue-600" />
    },
    {
      title: "Community Leadership",
      duration: "6 weeks",
      description: "Develop skills in cooperative management, knowledge sharing, project planning, and resource optimization.",
      topics: [
        "Cooperative management",
        "Knowledge sharing",
        "Project planning",
        "Resource optimization"
      ],
      icon: <Users className="h-6 w-6 text-amber-600" />
    },
    {
      title: "Value-Added Processing",
      duration: "10 weeks",
      description: "Master techniques for creating value-added products from agricultural harvests to increase farm income.",
      topics: [
        "Product development",
        "Quality control",
        "Packaging and branding",
        "Market analysis"
      ],
      icon: <Network className="h-6 w-6 text-purple-600" />
    }
  ],
  impact: {
    trainedFarmers: 1200,
    activeCourses: 8,
    certifications: 450,
    communityProjects: 15
  },
  testimonials: [
    {
      quote: "The training at REGEN Campus changed everything for me. I've increased my farm's yield by 45% while actually reducing water usage through the techniques I learned.",
      name: "Sarah Nantaba",
      role: "Coffee Farmer, Uganda",
      image: "https://images.unsplash.com/photo-1597680872640-8dbde8d0a5c0?q=80&w=300&h=300&auto=format&fit=crop"
    },
    {
      quote: "What sets REGEN Campus apart is the perfect blend of traditional wisdom and cutting-edge technology. The IoT sensor training has revolutionized how I monitor soil health.",
      name: "David Okello",
      role: "Technology Coordinator, Mbale Cooperative",
      image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=300&h=300&auto=format&fit=crop"
    },
    {
      quote: "Learning about regenerative agriculture at REGEN Campus gave me the tools to address climate change impacts on our farm. Now we're more resilient than ever.",
      name: "Maria Kizza",
      role: "Agroforestry Specialist",
      image: "https://images.unsplash.com/photo-1544717305-2782549b5136?q=80&w=300&h=300&auto=format&fit=crop"
    }
  ],
  facultyMembers: [
    {
      name: "Prof. James Mburu",
      role: "Director of Agroecology",
      bio: "Expert in regenerative farming practices with over 20 years of experience across East Africa",
      image: "https://images.unsplash.com/photo-1530268729831-4b0b9e170218?q=80&w=300&h=300&auto=format&fit=crop"
    },
    {
      name: "Dr. Lisa Ndungu",
      role: "Head of Technology Integration",
      bio: "PhD in Agricultural Technology with specialization in IoT applications for smallholder farmers",
      image: "https://images.unsplash.com/photo-1581992652564-44c42f5ad3ad?q=80&w=300&h=300&auto=format&fit=crop"
    },
    {
      name: "Edward Mutesa",
      role: "Master Farmer & Community Coordinator",
      bio: "Founder of Edward's Hamlet and expert in community-based regenerative farming",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&h=300&auto=format&fit=crop"
    },
    {
      name: "Dr. Emily Wairimu",
      role: "Soil Science Specialist",
      bio: "Researcher focused on microbial soil health and carbon sequestration techniques",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=300&h=300&auto=format&fit=crop"
    }
  ],
  upcomingCourses: [
    {
      title: "Agroforestry Fundamentals",
      startDate: "June 15, 2025",
      format: "Hybrid (In-person & Online)",
      spaces: "12 spaces left"
    },
    {
      title: "DIY Farm Sensor Workshop",
      startDate: "July 3, 2025",
      format: "In-person at Edward's Hamlet",
      spaces: "8 spaces left"
    },
    {
      title: "Sustainable Water Management",
      startDate: "July 20, 2025",
      format: "Online with practical components",
      spaces: "15 spaces left"
    }
  ]
};

const REGENCampusPage: React.FC = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interests: [],
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      setFormError('Please provide your name and email');
      return;
    }
    
    setSubmitting(true);
    setFormError('');
    
    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setFormSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        interests: [],
        message: ''
      });
      
      // Reset success message after delay
      setTimeout(() => {
        setFormSuccess(false);
      }, 5000);
    }, 1500);
  };

  const handleInterestToggle = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest) 
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="relative bg-green-900 text-white">
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1516321497487-e288fb19713f" 
            alt="Students learning in outdoor classroom"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 py-20 relative z-10">
          <AnimatedSection>
            <div className="max-w-4xl">
              <Badge 
                variant="green" 
                className="inline-flex mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 text-white"
                icon={<School className="h-5 w-5" />}
              >
                Educational Hub
              </Badge>
              <h1 className="text-5xl font-bold mb-6">
                REGEN Campus: Transforming Agricultural Education
              </h1>
              <p className="text-xl opacity-90 mb-8 leading-relaxed">
                Our innovative learning environment combines traditional wisdom with cutting-edge technology, 
                creating a new generation of regenerative agriculture leaders.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700"
                >
                  <a href="#programs" className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    <span>Explore Programs</span>
                  </a>
                </Button>
                <Button 
                  variant="outline"  
                  size="lg"
                  className="bg-transparent text-white border-white hover:bg-white/10"
                >
                  <a href="#enroll" className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Enrollment Info</span>
                  </a>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
      </div>

      {/* Campus Overview */}
      <div className="container mx-auto px-4 py-16">
        <AnimatedSection>
          <div className="max-w-4xl mx-auto text-center mb-16">
            <Badge 
              variant="green" 
              className="mb-4"
              icon={<School className="h-4 w-4" />}
            >
              About REGEN Campus
            </Badge>
            <h2 className="text-4xl font-bold mb-6">A New Approach to Agricultural Education</h2>
            <p className="text-lg text-gray-600">
              {campusData.overview.description} {campusData.overview.mission}
            </p>
          </div>
        </AnimatedSection>

        {/* Key Features */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
          <AnimatedSection delay={0.1}>
            <Card className="h-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sprout className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Hands-On Learning</h3>
                <p className="text-gray-600">
                  Learn by doing in our demonstration farms and experimental plots, directly applying 
                  regenerative techniques in real agricultural settings.
                </p>
              </div>
            </Card>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <Card className="h-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Brain className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Technology Integration</h3>
                <p className="text-gray-600">
                  Access cutting-edge agricultural technology including IoT sensors, data analytics, 
                  and AI-powered decision support systems.
                </p>
              </div>
            </Card>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <Card className="h-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Community Network</h3>
                <p className="text-gray-600">
                  Join a global network of farmers, researchers, and practitioners sharing knowledge 
                  and supporting each other's regenerative agriculture journeys.
                </p>
              </div>
            </Card>
          </AnimatedSection>
        </div>

        {/* Campus Location */}
        <div className="mb-20">
          <SectionHeader
            title="Our Campus Locations"
            subtitle="Experience the REGEN Campus difference at our state-of-the-art learning centers"
            icon={<MapPin className="h-5 w-5" />}
            badgeText="Locations"
            badgeColor="green"
          />

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto mt-12">
            <AnimatedSection>
              <div className="h-96 rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e" 
                  alt="Edward's Hamlet Campus"
                  className="w-full h-full object-cover"
                />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div>
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-green-600" />
                  <span>{campusData.locations[0].name}</span>
                </h3>
                <p className="text-gray-600 mb-6">
                  Our flagship campus located in the heart of Uganda's agricultural region. 
                  Edward's Hamlet serves as both a regenerative farm and an educational hub, 
                  showcasing sustainable farming practices in action.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="h-4 w-4 text-green-600" />
                      <span>{campusData.locations[0].location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Leaf className="h-4 w-4 text-green-600" />
                      <span>{campusData.locations[0].size}</span>
                    </div>
                  </div>
                </div>
                
                <h4 className="font-semibold mb-3">Campus Features:</h4>
                <ul className="space-y-2">
                  {campusData.locations[0].features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-green-600" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link 
                  to="/edwards-hamlet"
                  className="inline-flex items-center gap-2 mt-6 text-green-600 font-medium hover:text-green-700"
                >
                  <span>Learn more about Edward's Hamlet</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>

        {/* Programs Section */}
        <div id="programs" className="mb-20">
          <SectionHeader
            title="Educational Programs"
            subtitle="Comprehensive courses designed to develop practical skills and knowledge in sustainable agriculture"
            icon={<BookOpen className="h-5 w-5" />}
            badgeText="Curriculum"
            badgeColor="amber"
          />

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto mt-12">
            {campusData.programs.map((program, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <Card className="h-full">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      {program.icon}
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-bold">{program.title}</h3>
                        <Badge variant={index === 0 ? 'green' : index === 1 ? 'blue' : index === 2 ? 'amber' : 'purple'}>
                          {program.duration}
                        </Badge>
                      </div>
                      <p className="text-gray-600 my-3">
                        {program.description}
                      </p>
                      <div>
                        <h4 className="font-medium mb-2">Key Topics:</h4>
                        <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                          {program.topics.map((topic, topicIndex) => (
                            <li key={topicIndex} className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span className="text-sm">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>
              </AnimatedSection>
            ))}
          </div>
          
          <div className="mt-10 flex justify-center">
            <Link
              to="#enroll"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Users className="h-5 w-5" />
              <span>Enroll in a Program</span>
            </Link>
          </div>
        </div>

        {/* Impact Section */}
        <div className="mb-20 bg-white py-16 rounded-xl shadow-sm">
          <div className="container mx-auto px-4">
            <SectionHeader
              title="Our Impact"
              subtitle="Transforming agriculture through education and practical skills development"
              icon={<Award className="h-5 w-5" />}
              badgeText="Results"
              badgeColor="green"
            />

            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto mt-12">
              <AnimatedSection delay={0.1}>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-all"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="text-4xl font-bold text-green-600 mb-1">
                    {campusData.impact.trainedFarmers.toLocaleString()}
                  </div>
                  <div className="text-gray-600">Farmers Trained</div>
                </motion.div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.15}>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-all"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="text-4xl font-bold text-blue-600 mb-1">
                    {campusData.impact.activeCourses}
                  </div>
                  <div className="text-gray-600">Active Courses</div>
                </motion.div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-all"
                >
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="h-8 w-8 text-amber-600" />
                  </div>
                  <div className="text-4xl font-bold text-amber-600 mb-1">
                    {campusData.impact.certifications.toLocaleString()}
                  </div>
                  <div className="text-gray-600">Certifications Issued</div>
                </motion.div>
              </AnimatedSection>
              
              <AnimatedSection delay={0.25}>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-all"
                >
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Globe className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="text-4xl font-bold text-purple-600 mb-1">
                    {campusData.impact.communityProjects}
                  </div>
                  <div className="text-gray-600">Community Projects</div>
                </motion.div>
              </AnimatedSection>
            </div>
            
            <div className="max-w-2xl mx-auto mt-12 text-center">
              <p className="text-gray-600">
                Our graduates have implemented sustainable practices on over 5,000 acres of farmland, 
                resulting in an average 45% increase in yield and 30% reduction in water usage.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-20">
          <SectionHeader
            title="Student Testimonials"
            subtitle="Hear from the farmers and agricultural professionals who have experienced REGEN Campus"
            icon={<Star className="h-5 w-5" />}
            badgeText="Success Stories"
            badgeColor="green"
          />

          <div className="max-w-4xl mx-auto mt-12">
            <div className="relative">
              <div className="overflow-hidden rounded-xl">
                {campusData.testimonials.map((testimonial, idx) => (
                  <motion.div
                    key={idx}
                    className="absolute inset-0"
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: activeTestimonial === idx ? 1 : 0,
                      x: activeTestimonial === idx ? 0 : (activeTestimonial > idx ? -100 : 100)
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <Card className="h-full flex flex-col md:flex-row items-center gap-8 p-8">
                      <div className="w-28 h-28 rounded-full overflow-hidden flex-shrink-0">
                        <img 
                          src={testimonial.image} 
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-lg italic text-gray-700 mb-6 relative">
                          <span className="absolute -top-4 -left-2 text-4xl text-green-200">"</span>
                          {testimonial.quote}
                          <span className="absolute -bottom-4 -right-2 text-4xl text-green-200">"</span>
                        </p>
                        <div>
                          <p className="font-bold text-gray-800">{testimonial.name}</p>
                          <p className="text-gray-600">{testimonial.role}</p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
              
              <div className="flex justify-center gap-2 mt-10">
                {campusData.testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      activeTestimonial === idx 
                        ? 'bg-green-600' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`View testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Faculty Members */}
        <div className="mb-20">
          <SectionHeader
            title="Our Faculty"
            subtitle="Meet the experts who will guide your learning journey"
            icon={<Users className="h-5 w-5" />}
            badgeText="Instructors"
            badgeColor="blue"
          />

          <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto mt-12">
            {campusData.facultyMembers.map((member, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-1">{member.name}</h3>
                    <div className="text-green-600 text-sm mb-3">{member.role}</div>
                    <p className="text-gray-600 text-sm">{member.bio}</p>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>

        {/* Upcoming Courses */}
        <div className="mb-20">
          <SectionHeader
            title="Upcoming Courses"
            subtitle="Join our next cohort of sustainable agriculture learners"
            icon={<Calendar className="h-5 w-5" />}
            badgeText="Enrollment Open"
            badgeColor="green"
          />

          <div className="max-w-4xl mx-auto mt-12">
            <Card className="p-0 overflow-hidden">
              <div className="bg-green-50 p-6 border-b border-green-100">
                <h3 className="text-xl font-bold text-green-800">Course Schedule</h3>
                <p className="text-green-700">Upcoming classes you can join today</p>
              </div>
              
              <div className="divide-y">
                {campusData.upcomingCourses.map((course, index) => (
                  <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h4 className="font-bold text-lg">{course.title}</h4>
                        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 text-green-600" />
                            <span>Starts {course.startDate}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Globe className="h-4 w-4 text-green-600" />
                            <span>{course.format}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant="amber">{course.spaces}</Badge>
                        <Link 
                          to="#enroll" 
                          className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg flex items-center gap-1 transition-colors"
                        >
                          <span>Enroll</span>
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-6 bg-gray-50">
                <div className="flex items-start gap-3">
                  <Lightbulb className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Custom Training Programs</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      We also offer tailored training programs for organizations and cooperatives. 
                      <Link to="#enroll" className="text-green-600 hover:text-green-700 ml-1 font-medium">
                        Contact us
                      </Link> to discuss your specific needs.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Enrollment Section */}
        <div id="enroll" className="mb-20">
          <SectionHeader
            title="Get Started at REGEN Campus"
            subtitle="Take the first step towards mastering regenerative agriculture"
            icon={<Users className="h-5 w-5" />}
            badgeText="Enrollment"
            badgeColor="green"
          />

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto mt-12">
            <AnimatedSection>
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                    <Users className="h-6 w-6 text-green-600" />
                    <span>Who Should Apply</span>
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <span className="font-medium">Active Farmers</span>
                        <p className="text-gray-600 text-sm">Currently working in agriculture with a desire to implement sustainable practices</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <span className="font-medium">Agricultural Professionals</span>
                        <p className="text-gray-600 text-sm">Extension officers, consultants, and others working in the agricultural sector</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <span className="font-medium">Cooperative Leaders</span>
                        <p className="text-gray-600 text-sm">Those in leadership positions within farming cooperatives and associations</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <span className="font-medium">Agri-Entrepreneurs</span>
                        <p className="text-gray-600 text-sm">Individuals looking to start or expand agricultural businesses with sustainable practices</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-600" />
                    <span>Scholarships & Funding</span>
                  </h3>
                  <p className="text-gray-600 mb-4">
                    We're committed to making REGEN Campus accessible to all who are passionate about regenerative agriculture. 
                    Several scholarship and funding options are available:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Star className="h-4 w-4 text-amber-600 mt-1" />
                      <span>Full scholarships for smallholder farmers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Star className="h-4 w-4 text-amber-600 mt-1" />
                      <span>Partial funding through agricultural development programs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Star className="h-4 w-4 text-amber-600 mt-1" />
                      <span>Cooperative sponsorship opportunities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Star className="h-4 w-4 text-amber-600 mt-1" />
                      <span>Work-study programs at our campus locations</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="h-6 w-6 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-green-800">Have Questions?</h4>
                      <p className="text-green-700 text-sm mt-1">
                        Contact our admissions team for more information about programs, 
                        eligibility, and enrollment process.
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-green-800">
                        <Mail className="h-4 w-4" />
                        <a href="mailto:education@avnearth.com" className="text-green-700 hover:text-green-800 underline">
                          education@avnearth.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <Card className="bg-white p-8">
                <h3 className="text-2xl font-bold mb-6">Request Information</h3>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Programs of Interest
                    </label>
                    <div className="space-y-2">
                      {campusData.programs.map((program, index) => (
                        <label key={index} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-green-600 rounded focus:ring-green-500"
                            checked={formData.interests.includes(program.title)}
                            onChange={() => handleInterestToggle(program.title)}
                          />
                          <div>
                            <div className="font-medium">{program.title}</div>
                            <div className="text-xs text-gray-500">{program.duration}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message (Optional)
                    </label>
                    <textarea
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Tell us about your background and what you hope to learn..."
                    />
                  </div>
                  
                  {formError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-red-700">{formError}</p>
                    </div>
                  )}
                  
                  {formSuccess && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-green-700">
                        Thank you for your interest! We'll be in touch with more information about our programs.
                      </p>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    By submitting this form, you agree to receive communications about REGEN Campus and our educational offerings. We respect your privacy and will never share your information.
                  </div>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <motion.div 
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        >
                          <Send className="h-5 w-5" />
                        </motion.div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        <span>Request Information</span>
                      </>
                    )}
                  </button>
                </form>
              </Card>
            </AnimatedSection>
          </div>
        </div>

        {/* Campus Values */}
        <div className="mb-20">
          <div className="bg-gradient-to-r from-green-900 to-green-800 rounded-xl text-white overflow-hidden">
            <div className="p-12">
              <AnimatedSection>
                <div className="text-center mb-12">
                  <Badge 
                    variant="white" 
                    className="mb-4 bg-white/20 text-white"
                    icon={<Heart className="h-4 w-4" />}
                  >
                    Our Philosophy
                  </Badge>
                  <h2 className="text-4xl font-bold mb-6">REGEN Campus Values</h2>
                  <p className="text-xl opacity-90 max-w-3xl mx-auto">
                    Our educational approach is guided by these core principles that inform everything we do.
                  </p>
                </div>
              </AnimatedSection>

              <div className="grid md:grid-cols-3 gap-8">
                <AnimatedSection delay={0.1}>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-white/20 rounded-full mt-1">
                        <Globe className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl mb-2">Regenerative Thinking</h3>
                        <p className="opacity-90">
                          We believe in agricultural practices that not only sustain but actively regenerate ecosystems 
                          and communities, leaving them healthier than we found them.
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
                
                <AnimatedSection delay={0.2}>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-white/20 rounded-full mt-1">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl mb-2">Knowledge Integration</h3>
                        <p className="opacity-90">
                          We honor and integrate both traditional agricultural wisdom and modern scientific 
                          understanding to create holistic solutions.
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
                
                <AnimatedSection delay={0.3}>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-white/20 rounded-full mt-1">
                        <Zap className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl mb-2">Accessible Innovation</h3>
                        <p className="opacity-90">
                          We make cutting-edge agricultural technology accessible to all farmers, 
                          regardless of farm size or resources.
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-20 max-w-4xl mx-auto">
          <SectionHeader
            title="Frequently Asked Questions"
            subtitle="Answers to common questions about REGEN Campus"
            icon={<HelpCircle className="h-5 w-5" />}
            badgeText="FAQs"
            badgeColor="green"
          />

          <div className="mt-12 space-y-6">
            <AnimatedSection>
              <details className="group bg-white rounded-xl shadow-sm overflow-hidden">
                <summary className="flex justify-between items-center p-6 cursor-pointer">
                  <h3 className="text-lg font-semibold">What are the admission requirements?</h3>
                  <div className="ml-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full border border-green-600 text-green-600 group-open:rotate-180 transition-transform">
                      <ChevronRight className="h-4 w-4 group-open:transform group-open:rotate-90" />
                    </div>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p className="mb-3">
                    There are no strict academic requirements to join REGEN Campus programs. We believe in accessible education and evaluate applicants based on:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Commitment to sustainable agriculture principles</li>
                    <li>Willingness to implement learned practices</li>
                    <li>Desire to share knowledge with their communities</li>
                    <li>Basic literacy and numeracy skills (support is available)</li>
                  </ul>
                  <p className="mt-3">
                    We offer programs in multiple languages and provide additional support for those with limited formal education backgrounds.
                  </p>
                </div>
              </details>
            </AnimatedSection>
            
            <AnimatedSection delay={0.1}>
              <details className="group bg-white rounded-xl shadow-sm overflow-hidden">
                <summary className="flex justify-between items-center p-6 cursor-pointer">
                  <h3 className="text-lg font-semibold">How much does it cost to attend REGEN Campus?</h3>
                  <div className="ml-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full border border-green-600 text-green-600 group-open:rotate-180 transition-transform">
                      <ChevronRight className="h-4 w-4 group-open:transform group-open:rotate-90" />
                    </div>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p className="mb-3">
                    Program costs vary depending on the course duration, format, and your background:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Scholarships cover 100% of costs for qualifying smallholder farmers</li>
                    <li>Sliding scale fees based on income and organization type</li>
                    <li>Cooperative member discounts available</li>
                    <li>Subsidized rates for participants from low-income regions</li>
                  </ul>
                  <p className="mt-3">
                    Our goal is to ensure that financial constraints never prevent dedicated individuals from accessing quality agricultural education. Contact our admissions team for your specific pricing options.
                  </p>
                </div>
              </details>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <details className="group bg-white rounded-xl shadow-sm overflow-hidden">
                <summary className="flex justify-between items-center p-6 cursor-pointer">
                  <h3 className="text-lg font-semibold">Can I attend courses online or must I be physically present?</h3>
                  <div className="ml-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full border border-green-600 text-green-600 group-open:rotate-180 transition-transform">
                      <ChevronRight className="h-4 w-4 group-open:transform group-open:rotate-90" />
                    </div>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    We offer three learning formats to accommodate different needs:
                  </p>
                  <ol className="list-decimal pl-6 space-y-2 my-3">
                    <li>
                      <strong>In-person immersive programs</strong>: Full-time, hands-on learning at our campus locations with direct field experience and community engagement.
                    </li>
                    <li>
                      <strong>Hybrid programs</strong>: Combines online theoretical learning with periodic in-person practical modules at our campus or partner locations.
                    </li>
                    <li>
                      <strong>Fully online programs</strong>: Digital courses with virtual mentorship and support for implementing practices on your own farm.
                    </li>
                  </ol>
                  <p>
                    Each format is designed to deliver practical, applicable knowledge that can be immediately implemented in your agricultural context. The best format depends on your learning style, location, and schedule constraints.
                  </p>
                </div>
              </details>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <details className="group bg-white rounded-xl shadow-sm overflow-hidden">
                <summary className="flex justify-between items-center p-6 cursor-pointer">
                  <h3 className="text-lg font-semibold">Do I get a certificate after completing a program?</h3>
                  <div className="ml-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full border border-green-600 text-green-600 group-open:rotate-180 transition-transform">
                      <ChevronRight className="h-4 w-4 group-open:transform group-open:rotate-90" />
                    </div>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p className="mb-3">
                    Yes, all graduates receive a REGEN Campus certificate upon successful completion of their program. Our certifications are:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Recognized by agricultural organizations and cooperatives</li>
                    <li>Verified through blockchain technology for authenticity</li>
                    <li>Evidence-based, requiring demonstrated practical application</li>
                    <li>Eligible for continuing education credits in many regions</li>
                  </ul>
                  <p className="mt-3">
                    Each certificate details the specific skills and knowledge areas mastered, along with documented implementation projects completed during the program.
                  </p>
                </div>
              </details>
            </AnimatedSection>
            
            <AnimatedSection delay={0.4}>
              <details className="group bg-white rounded-xl shadow-sm overflow-hidden">
                <summary className="flex justify-between items-center p-6 cursor-pointer">
                  <h3 className="text-lg font-semibold">How can my organization partner with REGEN Campus?</h3>
                  <div className="ml-4">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full border border-green-600 text-green-600 group-open:rotate-180 transition-transform">
                      <ChevronRight className="h-4 w-4 group-open:transform group-open:rotate-90" />
                    </div>
                  </div>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p className="mb-3">
                    We welcome partnerships with organizations committed to sustainable agriculture:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>
                      <strong>Educational partners</strong>: Joint curriculum development, faculty exchanges, and combined research initiatives
                    </li>
                    <li>
                      <strong>Corporate sponsors</strong>: Fund scholarships, campus facilities, or specialized training programs
                    </li>
                    <li>
                      <strong>Community organizations</strong>: Collaborate on outreach programs and knowledge dissemination
                    </li>
                    <li>
                      <strong>Technology providers</strong>: Integrate innovative tools into our educational programs
                    </li>
                  </ul>
                  <p className="mt-3">
                    Contact our partnerships team at <a href="mailto:partnerships@avnearth.com" className="text-green-600 hover:underline">partnerships@avnearth.com</a> to discuss collaboration opportunities.
                  </p>
                </div>
              </details>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-green-900 py-20 text-white">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold mb-6">Begin Your Regenerative Journey</h2>
              <p className="text-xl opacity-90 mb-8">
                Join a community of change-makers creating a more sustainable agricultural future.
                Our next enrollment period closes soon!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="#enroll"
                  className="bg-white text-green-900 px-8 py-4 rounded-lg hover:bg-green-50 transition-colors inline-flex items-center justify-center gap-2 font-medium"
                >
                  <Send className="h-5 w-5" />
                  <span>Request Information</span>
                </Link>
                <a
                  href="https://calendly.com/regen-campus/info-session"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2 font-medium"
                >
                  <Calendar className="h-5 w-5" />
                  <span>Schedule a Call</span>
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </div>
  );
};

export default REGENCampusPage;